import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';
import './css/Cliente.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import { formatCurrency, formatQuantidade } from './utils/formatMoeda';
import { calculateTotal } from './utils/calculateUtil';
import { formatString } from './utils/StringUtils';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import PcaSummary from './components/PcaSummary';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import LoadingModal from './components/modals/LoadingModal';

function Pca() {
  const [pcas, setPcas] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumo');
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const totalItems = pcas.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedPcasForPage = pcas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const toggleExpand = (rowKey) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  useEffect(() => {
    const fetchPcas = async () => {
      setLoading(true);
      setSummaryData(null);
      const token = localStorage.getItem('token');
      const cnpj = localStorage.getItem('cnpj');
      const anoBase = localStorage.getItem('anoBase');
      try {
          await axios.post(`${API_BASE_URL.PCA}/consolidar`, null, {
            params: { cnpj, anoBase },
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          });

        const result = await axios.get(`${API_BASE_URL.PCA}/all`, {
          params: { cnpj, anoBase },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        const fetchedPcas = result.data || [];
        setPcas(fetchedPcas);
        const totals = {
          materiais: 0, servicos: 0, obras: 0, servicosEngenharia: 0,
          locacaoImoveis: 0, alienacaoPermissao: 0, obrasServicos: 0, tic: 0, totalGeral: 0,
        };
        fetchedPcas.forEach(item => {
          const itemTotal = parseFloat(item.qtdeEstimada || 0) * parseFloat(item.vlrUnitario || 0);
          totals.totalGeral += itemTotal;
          const tipo = item.categoria;
          if (tipo === 'Material') totals.materiais += itemTotal;
          else if (tipo === 'Serviço') totals.servicos += itemTotal;
          else if (tipo === 'Obras') totals.obras += itemTotal;
          else if (tipo === 'Soluções de TIC') totals.tic += itemTotal;
          else if (tipo === 'Serviços de Engenharia') totals.servicosEngenharia += itemTotal;
          else if (tipo === 'Locação de Imóveis') totals.locacaoImoveis += itemTotal;
          else if (tipo === 'Alienação/Concessão/Permissão') totals.alienacaoPermissao += itemTotal;
          else if (tipo === 'Obras e Serviços de Engenharia') totals.obrasServicos += itemTotal;
        });
        setSummaryData(totals);
      } catch (error) {
        setPcas([]);
        setSummaryData({
          materiais: 0, servicos: 0, obras: 0, servicosEngenharia: 0,
          locacaoImoveis: 0, alienacaoPermissao: 0, obrasServicos: 0, tic: 0, totalGeral: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPcas();
  }, []);

  const renderSummaryContent = () => {
    if (isLoading || summaryData === null) {
      return null;
    }
    return <PcaSummary data={summaryData} />;
  };

  return (
    <Layout>
      <LoadingModal loading={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="PCA - Plano de Contratação Anual" icon="bi-file-earmark-text" backButton={false} closeRoute="/home" />
        <PageContent style={{ paddingBottom: 0 }}>
      <div
        className="d-flex justify-content-between w-100 small"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Nav Tabs */}
        <ul className="nav nav-tabs border-bottom" role="tablist" style={{ padding: '0.15rem 0.35rem', flexShrink: 0 }}>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'resumo' ? 'active' : ''}`}
              onClick={() => setActiveTab('resumo')}
              type="button"
              role="tab"
              style={{ padding: '0.25rem 0.6rem' }}
            >
              <i className="bi bi-pie-chart-fill me-2"></i>Resumo
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'tabela' ? 'active' : ''}`}
              onClick={() => setActiveTab('tabela')}
              type="button"
              role="tab"
              style={{ padding: '0.25rem 0.6rem' }}
            >
              <i className="bi bi-table me-2"></i>Tabela
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="p-1" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Aba Resumo */}
          {activeTab === 'resumo' && (
            <div role="tabpanel" style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {renderSummaryContent()}
            </div>
          )}

          {/* Aba Tabela */}
          {activeTab === 'tabela' && (
            <div role="tabpanel" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              <TableContainer tableClass="table-custom-radius">
                <TableHeader columns={[
                  { label: 'ID' },
                  { label: 'Nº DFD' },
                  { label: 'Tipo de Demanda', width: '16%' },
                  { label: 'Área', width: '14%' },
                  { label: 'Tipo de Item', width: '12%' },
                  { label: 'Descrição' },
                  { label: 'Qtde', width: '6%', align: 'right' },
                  { label: 'Valor', width: '8%', align: 'right' },
                  { label: 'Total', width: '8%', align: 'right' },
                ]} />
                <tbody>
                  {isLoading ? null : selectedPcasForPage.length > 0 ? (
                    selectedPcasForPage.map(c => (
                      <tr key={`${c.cnpj}-${c.anoPca}-${c.id}`}>
                        <td style={{ width: '3%' }}>{c.id}</td>
                        <td style={{ width: '7%' }}>{c.dfdNum ? formatString(c.dfdNum, 4) : '-'}</td>
                        <td style={{ width: '16%' }}>{c.tipoDemanda}</td>
                        <td style={{ width: '14%' }}>{c.secretariaDepartamento}</td>
                        <td style={{ width: '12%' }}>{c.tipoItem}</td>
                        <td>
                          {(() => {
                            const rowKey = `${c.cnpj}-${c.anoPca}-${c.id}`;
                            const desc = c.descricaoItem || '';
                            const isExpanded = !!expandedRows[rowKey];
                            const limit = 80;
                            const shortDesc = desc.length > limit ? desc.slice(0, limit) + '...' : desc;
                            return (
                              <>
                                {desc.length > limit && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 me-1 align-middle"
                                      style={{ verticalAlign: 'middle', color: '#000' }}
                                    onClick={(e) => { e.stopPropagation(); toggleExpand(rowKey); }}
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Recolher descrição' : 'Expandir descrição'}
                                  >
                                    <i className={`bi ${isExpanded ? 'bi-dash-circle' : 'bi-plus-circle'}`} aria-hidden="true"></i>
                                  </button>
                                )}
                                <span style={{ whiteSpace: 'pre-wrap', display: 'inline' }}>{isExpanded ? desc : shortDesc}</span>
                              </>
                            );
                          })()}
                        </td>
                        <td className="text-end" style={{ width: '6%' }}>{formatQuantidade(c.qtdeEstimada)}</td>
                        <td className="text-end" style={{ width: '8%' }}>
                          {formatCurrency(c.vlrUnitario)}
                        </td>
                        <td className="text-end" style={{ width: '8%' }}>
                          {calculateTotal(c.qtdeEstimada, c.vlrUnitario)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center p-4">
                        Nenhum registro encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </TableContainer>
              {!isLoading && pcas.length > 0 && (
                <PageFooter style={{ marginTop: '1rem' }}>
                  <Pagination10 {...setPagination} />
                </PageFooter>
              )}
            </div>
          )}
        </div>
      </div>
          <DefaultModal {...setDefaultModal} />
          <ConfirmModal {...setConfirmModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default Pca;
